<template>
  <StandardCard
    @action-clicked="handleCardAction"
    :actions="useOverflowActionMenu ? [] : cardActions"
    :class="{ 'mb-12': reserveSpaceBelowForHelpFlyout }"
    :processing="processing"
    card-test-id="danger-bar"
  >
    <span
      ><span class="fw-800">{{ $bct('Be careful!') }}</span
      >{{ $bct(' Some of these actions cannot be undone.') }}</span
    >

    <template
      v-if="useOverflowActionMenu"
      #actions
    >
      <v-card-actions class="d-flex justify-space-between align-center w-100 pa-4">
        <StandardButton
          v-if="deletable"
          @click="handleCardAction({ action: 'destroy' })"
          :disabled-text="disabledText"
          :loading="processing"
          data-testid="danger-bar-delete"
          prepend-icon="delete"
          role="destructive"
          text="Delete"
        />

        <div
          class="d-flex align-center ga-3 ms-auto"
          data-testid="danger-bar-overflow-actions"
        >
          <StandardChip
            v-if="publishable"
            :active="published"
            role="publishedChip"
          />

          <StandardChip
            v-if="archivable && archived"
            :label="false"
            color="grey-darken-3"
            text="Archived"
          />

          <v-menu
            v-model="actionsMenuOpen"
            :close-on-content-click="true"
            location="bottom end"
          >
            <template #activator="{ props: activatorProps }">
              <StandardButton
                :bind-props="activatorProps"
                :loading="processing"
                append-icon="expand_more"
                color="primary"
                text="Actions"
                variant="flat"
              />
            </template>

            <v-list
              class="b-1 py-0"
              data-testid="danger-bar-actions-list"
              tile
            >
              <template
                v-for="(action, index) in secondaryActions"
                :key="action.event"
              >
                <v-list-item
                  @click="onMenuItemClick(action)"
                  :data-testid="action.dataTestId"
                  :disabled="processing || !!action.disabledText"
                  :title="$bct(action.title)"
                >
                  <template
                    v-if="action.prependIcon"
                    #prepend
                  >
                    <v-icon
                      :icon="action.prependIcon"
                      class="material-icons-outlined"
                      size="20"
                    />
                  </template>
                </v-list-item>

                <v-divider
                  v-if="index + 1 < secondaryActions.length"
                  class="bc-black"
                />
              </template>
            </v-list>
          </v-menu>
        </div>
      </v-card-actions>
    </template>
  </StandardCard>
</template>

<script setup>
import { useStore } from 'vuex';

import useConfirmDialog from '@/shared/composables/useConfirmDialog';

const $bct = inject('translator');
const confirm = useConfirmDialog();
const store = useStore();

const MIN_ACTIONS_FOR_OVERFLOW_MENU = 3;

const props = defineProps({
  disabledText: {
    type: String,
    default: undefined,
  },
  onDuplicate: {
    type: [Array, Function],
    default: undefined,
  },
  otherActions: {
    default: () => [],
    type: Array,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  archivable: {
    type: Boolean,
    default: false,
  },
  archived: {
    type: Boolean,
    default: false,
  },
  publishable: {
    type: Boolean,
    default: false,
  },
  published: {
    type: Boolean,
    default: false,
  },
  refreshable: {
    type: Boolean,
    default: false,
  },
  deletable: {
    type: Boolean,
    default: true,
  },
  confirmDelete: {
    type: Boolean,
    default: false,
  },
  confirmationMessages: {
    type: Object,
    default: () => ({}),
  },
  confirmationOptions: {
    type: Object,
    default: () => ({}),
  },
  resourceName: {
    type: String,
    default: 'resource',
  },
});

const emit = defineEmits([
  'archive',
  'destroy',
  'duplicate',
  'publish',
  'refresh',
  'run',
  'unarchive',
  'unpublish',
]);

const ACTIONS_REQUIRING_CONFIRM = ['archive', 'refresh', 'unarchive'];

const actionsMenuOpen = ref(false);

const secondaryActions = computed(() => {
  const actions = [];

  if (props.onDuplicate) {
    actions.push({
      title: 'Duplicate',
      event: 'duplicate',
      color: 'info',
      prependIcon: 'content_copy',
      role: 'secondary',
    });
  }

  if (props.publishable && !props.published) {
    actions.push({
      title: 'Publish',
      event: 'publish',
      color: 'primary',
      prependIcon: 'publish',
      role: 'secondary',
    });
  }

  if (props.publishable && props.published) {
    actions.push({
      title: 'Unpublish',
      event: 'unpublish',
      color: 'primary',
      prependIcon: 'block',
      variant: 'outlined',
      role: 'secondary',
    });
  }

  if (props.archivable && !props.archived) {
    actions.push({
      title: 'Archive',
      event: 'archive',
      color: 'primary',
      prependIcon: 'archive',
      role: 'secondary',
    });
  }

  if (props.archivable && props.archived) {
    actions.push({
      title: 'Unarchive',
      event: 'unarchive',
      color: 'primary',
      prependIcon: 'archive',
      role: 'secondary',
    });
  }

  if (props.refreshable) {
    actions.push({
      title: 'Refresh',
      event: 'refresh',
      color: 'primary',
      prependIcon: 'refresh',
      role: 'secondary',
    });
  }

  actions.push(...props.otherActions);

  return actions;
});

const useOverflowActionMenu = computed(
  () => secondaryActions.value.length >= MIN_ACTIONS_FOR_OVERFLOW_MENU,
);

const reserveSpaceBelowForHelpFlyout = computed(
  () => store.state.config?.enable_help_documentation,
);

const cardActions = computed(() => {
  const actions = [];

  if (props.deletable) {
    actions.push({
      title: 'Delete',
      event: 'destroy',
      color: 'red',
      prependIcon: 'delete',
      disabledText: props.disabledText,
      role: 'destructive',
    });
  }

  actions.push(...secondaryActions.value);

  return actions;
});

function actionRequiresConfirm(actionEvent) {
  if (actionEvent === 'destroy') {
    return props.confirmDelete;
  }
  return ACTIONS_REQUIRING_CONFIRM.includes(actionEvent);
}

function confirmationMessage(actionEvent) {
  return (
    props.confirmationMessages[actionEvent] ??
    `Are you sure you want to ${actionEvent} ${props.resourceName}?`
  );
}

async function handleCardAction({ action: actionEvent }) {
  if (actionRequiresConfirm(actionEvent)) {
    const options = props.confirmationOptions[actionEvent];
    const confirmed = options
      ? await confirm(confirmationMessage(actionEvent), options)
      : await confirm(confirmationMessage(actionEvent));
    if (!confirmed) return;
  }

  emit(actionEvent);
}

function onMenuItemClick(action) {
  actionsMenuOpen.value = false;
  void handleCardAction({ action: action.event });
}
</script>
